import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {
  Box,
  Breadcrumbs,
  Divider,
  Link,
  Skeleton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import menuConfig from "src/configs/menu";

// ─── Helpers ────────────────────────────────────────────────────────────────

/**
 * Dado el level actual (e.g. "07.02.01.00") devuelve los segmentos del breadcrumb:
 *   { module, section, current }
 *
 * Estructura de levels:
 *   XX.YY.00.00  → sección padre (component: false)
 *   XX.YY.ZZ.00  → ítem de página
 */
function buildBreadcrumb(levelActual, currentPath) {
  if (!menuConfig || !menuConfig.length) return null;

  // Encontrar el ítem actual por level o por path
  let itemActual = menuConfig.find((item) => item.level === levelActual);
  if (!itemActual && currentPath) {
    itemActual = menuConfig.find((item) => item.path === currentPath);
  }
  if (!itemActual) return null;

  const [mod, sec] = itemActual.level.split(".");
  const sectionLevel = `${mod}.${sec}.00.00`;
  const sectionItem = menuConfig.find((item) => item.level === sectionLevel);

  return {
    module: itemActual.module?.trim() ?? null,
    section: sectionItem?.name?.trim() ?? null,
    current: itemActual.name?.trim() ?? null,
    path: itemActual.path ?? null,
  };
}

// ─── PageHeader ─────────────────────────────────────────────────────────────

/**
 * Componente de encabezado de página genérico y reutilizable.
 *
 * Props:
 *  - title      {string}        Título principal (requerido)
 *  - subtitle   {string}        Subtítulo opcional
 *  - icon       {ReactNode}     Ícono opcional (componente MUI Icon)
 *  - actions    {ReactNode}     Nodo opcional con botones de acción (derecha)
 *  - loading    {boolean}       Muestra skeleton mientras carga
 *  - sx         {object}        Estilos extra para el contenedor raíz
 *
 * Uso básico:
 *   <PageHeader title="Procesos General de Cajas" />
 *
 * Uso completo:
 *   <PageHeader
 *     title="Procesos General de Cajas"
 *     subtitle="Gestión y procesamiento de cajas activas"
 *     icon={<AllInboxIcon />}
 *     actions={<Button variant="contained">Nueva caja</Button>}
 *   />
 */
export const PageHeader = ({
  title,
  subtitle,
  icon,
  actions,
  loading = false,
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();

  const breadcrumb = useMemo(() => {
    const levelActual = localStorage.getItem("levelMenuActual");
    return buildBreadcrumb(levelActual, location.pathname);
  }, [location.pathname]);

  const titleColor =
    theme.palette.mode === "light" ? "#0A395F" : theme.palette.text.primary;

  return (
    <Box sx={{ mb: 3, ...sx }}>
      {/* ── Breadcrumbs ── */}
      {loading ? (
        <Skeleton width={240} height={20} sx={{ mb: 1 }} />
      ) : (
        <Breadcrumbs
          separator={<NavigateNextIcon sx={{ fontSize: 14 }} />}
          aria-label="breadcrumb"
          sx={{ mb: 1.25 }}
        >
          {/* Inicio */}
          <Link
            href="/dashboard"
            underline="hover"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.4,
              color: "text.secondary",
              fontSize: "0.75rem",
              fontFamily: '"Public Sans", sans-serif',
              "&:hover": { color: "primary.main" },
            }}
          >
            <HomeRoundedIcon sx={{ fontSize: 14 }} />
            Inicio
          </Link>

          {/* Módulo */}
          {breadcrumb?.module && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                fontFamily: '"Public Sans", sans-serif',
              }}
            >
              {breadcrumb.module}
            </Typography>
          )}

          {/* Sección */}
          {breadcrumb?.section && (
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "text.secondary",
                fontFamily: '"Public Sans", sans-serif',
              }}
            >
              {breadcrumb.section}
            </Typography>
          )}

          {/* Página actual */}
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: "text.primary",
              fontFamily: '"Public Sans", sans-serif',
            }}
          >
            {title}
          </Typography>
        </Breadcrumbs>
      )}

      {/* ── Fila título + acciones ── */}
      <Box
        sx={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        {/* Lado izquierdo: ícono + título + subtítulo */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
          {loading ? (
            <Skeleton variant="rounded" width={40} height={40} />
          ) : (
            icon && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor:
                    theme.palette.mode === "light"
                      ? "rgba(10,57,95,0.10)"
                      : "rgba(255,255,255,0.08)",
                  color: "primary.main",
                  flexShrink: 0,
                  "& svg": { fontSize: 22 },
                }}
              >
                {icon}
              </Box>
            )
          )}

          <Box sx={{ minWidth: 0 }}>
            {loading ? (
              <>
                <Skeleton width={220} height={28} />
                <Skeleton width={160} height={18} sx={{ mt: 0.25 }} />
              </>
            ) : (
              <>
                <Typography
                  variant="h6"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: "1rem", sm: "1.15rem" },
                    lineHeight: 1.3,
                    color: titleColor,
                    fontFamily: '"Public Sans", sans-serif',
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {title}
                </Typography>
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.8rem",
                      mt: 0.15,
                      fontFamily: '"Public Sans", sans-serif',
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* Lado derecho: acciones */}
        {!loading && actions && (
          <Box
            sx={{
              display: "flex",
              gap: 1,
              flexShrink: 0,
              flexWrap: "wrap",
              justifyContent: isMobile ? "flex-start" : "flex-end",
            }}
          >
            {actions}
          </Box>
        )}
      </Box>

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
};
